import React, { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { errMessage } from "../lib/errors";
import { X, Plus, Minus, ShoppingBag, Loader2, Tag, Check, Gift } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useTableSession } from "../context/TableSessionContext";
import { useToast } from "../hooks/use-toast";
import { menuHighlights, menuLookupKeys } from "../data/mock";
import { usePromoLanding, getReferralCode, clearReferralCode } from "../hooks/usePromoLanding";
import DeliveryZoneCheck from "./DeliveryZoneCheck";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UPSELL_IDS = [12, 11, 4, 3]; // Fried Plantains, Kala/Puff Puff, Chicken Skewers, Beef Skewers

function toNumber(p) {
  return parseFloat((p || "0").replace(/[^\d.]/g, "")) || 0;
}

export default function CartDrawer() {
  const { items, add, remove, setQty, total, count, open, setOpen } = useCart();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [orderType, setOrderType] = useState("takeaway"); // takeaway | dine_in | delivery
  const [tableNumber, setTableNumber] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryZone, setDeliveryZone] = useState(null);
  const [language, setLanguage] = useState("en"); // en | fr | kri
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { table, isPreOrder } = useTableSession();

  // Arrival window for pre-orders (minutes). Kitchen holds the ticket
  // until this timer is close, then fires — so food is hot on arrival.
  const [arrivalMinutes, setArrivalMinutes] = useState(30);

  // Refer-a-friend: read the stored MM-code (if any), validate against the
  // backend to prove it's a real code, then surface a $5-off banner.
  const [referralCode] = useState(() => getReferralCode());
  const [referralInfo, setReferralInfo] = useState(null); // {valid, discount, referrer_first_name}

  useEffect(() => {
    if (!referralCode) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/referrals/validate`, {
          code: referralCode,
          referee_email: email || undefined,
        });
        if (!cancelled) setReferralInfo(data.valid ? data : null);
      } catch (err) {
        console.warn("Referral validate failed", err?.response?.status || err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [referralCode, email]);

  // Landing-promo hook: if the user arrived via ?promo=WELCOME10 / ?ref=welcome,
  // pre-fill the code field so the discount is one-tap to apply.
  const landingPromo = usePromoLanding();

  const applyPromo = useCallback(async (codeOverride) => {
    const code = (codeOverride || promoCode).trim();
    if (!code) return;
    setPromoLoading(true);
    try {
      const { data } = await axios.post(`${API}/promo/validate`, { code });
      setPromo(data);
      setPromoCode(code);
      toast({ title: "Code applied", description: data.label });
    } catch (err) {
      setPromo(null);
      toast({ title: "Invalid code", description: errMessage(err, "Try WELCOME10.") });
    } finally {
      setPromoLoading(false);
    }
    // toast is stable from useToast; promoCode intentionally excluded because
    // codeOverride wins when provided.
  }, [promoCode, toast]);

  // Auto-fill + auto-apply landing promo once, the first time the drawer opens.
  useEffect(() => {
    if (!landingPromo || promo) return;
    if (!promoCode) setPromoCode(landingPromo);
    if (open) applyPromo(landingPromo);
  }, [landingPromo, open, promo, promoCode, applyPromo]);

  // If the guest scanned a table QR, force dine-in and pre-fill the table number.
  // For a "door" (pre-order) QR the table_number stays blank — the customer
  // fills it in on arrival and staff match by name.
  useEffect(() => {
    if (table) {
      setOrderType("dine_in");
      if (!isPreOrder) {
        setTableNumber((t) => t || String(table));
      }
    }
  }, [table, isPreOrder]);

  useEffect(() => {
    if (user && open) {
      setName((n) => n || user.name || "");
      setEmail((e) => e || user.email || "");
      setPhone((p) => p || user.phone || "");
    }
  }, [user, open]);

  const applyPromoManual = () => applyPromo();

  // Combo bundle savings — computed client-side to mirror the server's
  // priority order (Family → Kids → Base). If the customer has entered
  // their own promo we hide combo hints because Stripe accepts a single
  // discount per checkout.
  const combo = useMemo(() => {
    const empty = { family: 0, familyAmt: 0, kids: 0, kidsAmt: 0, base: 0, baseAmt: 0, total: 0 };
    if (promo) return empty;

    const FAMILY_KEY = "mamamina_bundle_family_feast";
    const FAMILY_MIN_DRINKS = 4;
    const FAMILY_DISCOUNT = 8; // $ per combo
    const KIDS_MEAL_KEYS = new Set(["mamamina_kids_meal"]);
    const KIDS_DRINK_KEYS = new Set([
      "mamamina_drink_kids_popper",
      "mamamina_drink_kids_fruit",
    ]);
    const KIDS_CAP = 10; // $ combined cap
    const BASE_PER_PAIR = 2; // $ per pair

    // Mutable pool of (lookup_key, qty, unit_price) so consumption doesn't
    // double-dip across tiers.
    const pool = items.map((it) => ({
      key: it.lookup_key || "",
      qty: it.quantity,
      unit: Number(it.price) || 0,
    }));
    const isDrink = (k) => (k || "").startsWith("mamamina_drink_");

    // Tier 1 — Family Combo
    const familyEntry = pool.find((p) => p.key === FAMILY_KEY);
    let totalDrinks = pool.filter((p) => isDrink(p.key)).reduce((s, p) => s + p.qty, 0);
    const familyCombos = familyEntry ? Math.min(familyEntry.qty, Math.floor(totalDrinks / FAMILY_MIN_DRINKS)) : 0;
    if (familyCombos > 0) {
      familyEntry.qty -= familyCombos;
      let need = familyCombos * FAMILY_MIN_DRINKS;
      for (const p of pool) {
        if (need <= 0) break;
        if (isDrink(p.key) && p.qty > 0) {
          const take = Math.min(p.qty, need);
          p.qty -= take;
          need -= take;
        }
      }
    }
    const familyAmt = familyCombos * FAMILY_DISCOUNT;

    // Tier 2 — Kids Combo
    const kidsMealsQty = pool.filter((p) => KIDS_MEAL_KEYS.has(p.key)).reduce((s, p) => s + p.qty, 0);
    const kidsDrinksQty = pool.filter((p) => KIDS_DRINK_KEYS.has(p.key)).reduce((s, p) => s + p.qty, 0);
    const kidsCombos = Math.min(kidsMealsQty, kidsDrinksQty);
    let kidsAmt = 0;
    if (kidsCombos > 0) {
      const mealTokens = [];
      for (const p of pool) if (KIDS_MEAL_KEYS.has(p.key)) for (let i = 0; i < p.qty; i++) mealTokens.push(p.unit);
      const drinkTokens = [];
      for (const p of pool) if (KIDS_DRINK_KEYS.has(p.key)) for (let i = 0; i < p.qty; i++) drinkTokens.push(p.unit);
      for (let i = 0; i < kidsCombos; i++) {
        const m = mealTokens.shift() || 0;
        const d = drinkTokens.shift() || 0;
        kidsAmt += Math.max(0, m + d - KIDS_CAP);
      }
      // Decrement pool
      let left = kidsCombos;
      for (const p of pool) {
        if (left <= 0) break;
        if (KIDS_MEAL_KEYS.has(p.key) && p.qty > 0) {
          const take = Math.min(p.qty, left);
          p.qty -= take;
          left -= take;
        }
      }
      left = kidsCombos;
      for (const p of pool) {
        if (left <= 0) break;
        if (KIDS_DRINK_KEYS.has(p.key) && p.qty > 0) {
          const take = Math.min(p.qty, left);
          p.qty -= take;
          left -= take;
        }
      }
    }

    // Tier 3 — Base
    let food = 0, drinks = 0;
    for (const p of pool) {
      if (p.qty <= 0) continue;
      if (isDrink(p.key)) drinks += p.qty;
      else food += p.qty;
    }
    const basePairs = Math.min(food, drinks);
    const baseAmt = basePairs * BASE_PER_PAIR;

    return {
      family: familyCombos,
      familyAmt,
      kids: kidsCombos,
      kidsAmt,
      base: basePairs,
      baseAmt,
      total: familyAmt + kidsAmt + baseAmt,
    };
  }, [items, promo]);

  const discount = promo ? (total * promo.percent_off) / 100 : combo.total;
  // Compute the delivery fee: normally the zone's flat fee, but drops to $0
  // once the discounted order clears the `free_over` threshold. Operator sets
  // this per zone from /admin → Delivery Zones.
  const totalPreDelivery = Math.max(0, total - discount);
  const deliveryFee = (() => {
    if (orderType !== "delivery" || !deliveryZone) return 0;
    if (deliveryZone.free_over > 0 && totalPreDelivery >= deliveryZone.free_over) return 0;
    return deliveryZone.fee || 0;
  })();
  const totalAfter = totalPreDelivery + deliveryFee;
  // Delivery is blocked entirely if the discounted subtotal is below the
  // per-zone minimum. UI surfaces this next to the CTA.
  const belowMinOrder =
    orderType === "delivery" &&
    deliveryZone &&
    deliveryZone.min_order > 0 &&
    totalPreDelivery < deliveryZone.min_order;

  const upsells = useMemo(() => {
    const inCart = new Set(items.map((i) => i.lookup_key));
    return menuHighlights
      .filter((m) => UPSELL_IDS.includes(m.id))
      .map((m) => ({ ...m, lookup_key: menuLookupKeys[m.id] }))
      .filter((m) => m.lookup_key && !inCart.has(m.lookup_key))
      .slice(0, 3);
  }, [items]);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      // Fold per-line special instructions (from the dish detail modal) into
      // the top-level notes field so mama's kitchen ticket surfaces them.
      const perLineNotes = items
        .filter((i) => i.notes)
        .map((i) => `${i.name}: ${i.notes}`)
        .join(" · ");
      const combinedNotes = [notes, perLineNotes].filter(Boolean).join(" — ");

      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_name: name || undefined,
        customer_email: email || undefined,
        customer_phone: phone || undefined,
        notes: combinedNotes || undefined,
        promo_code: promo?.code,
        order_type: orderType,
        table_number: orderType === "dine_in" ? (tableNumber || undefined) : undefined,
        delivery_address: orderType === "delivery" ? (deliveryAddress || undefined) : undefined,
        delivery_postcode: orderType === "delivery" ? (deliveryZone?.postcode || undefined) : undefined,
        is_preorder: isPreOrder || undefined,
        arrival_minutes: isPreOrder ? arrivalMinutes : undefined,
        referral_code: (referralInfo && referralCode) || undefined,
        language: language
      });
      if (data.checkout_url) {
        // Clear the referral code so refreshing the page doesn't
        // accidentally re-use it after Stripe returns.
        if (referralInfo && referralCode) clearReferralCode();
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Checkout failed",
        description: errMessage(err)
      });
      setLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[440px] bg-[var(--mm-bg)] border-l border-[var(--mm-line)] transform transition-transform duration-500 ${
          open ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
      >
        {/* Header — fixed */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--mm-line)] shrink-0">
          <div className="flex items-center gap-3">
            <ShoppingBag size={18} className="text-[var(--mm-accent)]" />
            <span className="section-label">Your Order</span>
            <span className="text-[var(--mm-cream-muted)] text-xs">({count})</span>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close cart" className="text-[var(--mm-cream)]">
            <X size={22} />
          </button>
        </div>

        {/* Scrollable body — everything except the pay footer */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="p-6">
            {items.length === 0 ? (
              <div className="text-center py-16">
                <div className="font-display italic text-2xl text-[var(--mm-cream)] mb-3">Your basket is empty</div>
                <p className="text-[var(--mm-cream-muted)] font-light text-sm">
                  Add a dish from the menu to get started.
                </p>
              </div>
            ) : (
              <ul className="space-y-4">
                {items.map((it) => (
                  <li key={it.lookup_key} className="flex gap-4 border-b border-[var(--mm-line)] pb-4">
                    {it.image && (
                      <img src={it.image} alt={it.name} className="w-20 h-20 object-cover shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <div className="font-display text-lg text-[var(--mm-cream)] leading-tight">{it.name}</div>
                        <div className="font-display text-[var(--mm-accent)] shrink-0">${(it.price * it.quantity).toFixed(2)}</div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-[var(--mm-line)]">
                          <button
                            onClick={() => setQty(it.lookup_key, it.quantity - 1)}
                            className="px-2 py-1 text-[var(--mm-cream)] hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-colors"
                            aria-label="Decrease"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="px-3 text-sm text-[var(--mm-cream)]">{it.quantity}</span>
                          <button
                            onClick={() => setQty(it.lookup_key, it.quantity + 1)}
                            className="px-2 py-1 text-[var(--mm-cream)] hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-colors"
                            aria-label="Increase"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <button
                          onClick={() => remove(it.lookup_key)}
                          className="text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {items.length > 0 && upsells.length > 0 && (
            <div className="border-t border-[var(--mm-line)] px-6 py-5">
              <div className="section-label mb-4">Add to your feast</div>
              <div className="space-y-2">
                {upsells.map((u) => (
                  <button
                    key={u.id}
                    onClick={() =>
                      add({
                        lookup_key: u.lookup_key,
                        name: u.name,
                        price: toNumber(u.price),
                        image: u.image
                      })
                    }
                    className="w-full flex items-center gap-3 p-2 border border-[var(--mm-line)] hover:border-[var(--mm-accent)] transition-colors text-left group"
                  >
                    <img src={u.image} alt={u.name} className="w-12 h-12 object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display italic text-[var(--mm-cream)] leading-tight truncate">{u.name}</div>
                      <div className="text-xs text-[var(--mm-cream-muted)]">{u.price}</div>
                    </div>
                    <span className="w-8 h-8 shrink-0 flex items-center justify-center border border-[var(--mm-accent)] text-[var(--mm-accent)] group-hover:bg-[var(--mm-accent)] group-hover:text-[var(--mm-bg)] transition-all">
                      <Plus size={14} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {items.length > 0 && (
            <div className="border-t border-[var(--mm-line)] p-6 space-y-3">
              {/* Checkout progress steps */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-[var(--mm-line)]">
                <Step done label="Menu" n={1} />
                <div className="flex-1 h-px bg-[var(--mm-accent)] mx-2" />
                <Step done label="Cart" n={2} />
                <div className="flex-1 h-px bg-[var(--mm-line)] mx-2" />
                <Step label="Pay" n={3} />
              </div>

              {/* Combo deal nudges — surface the biggest applicable upgrade first */}
              {!promo && (() => {
                const drinks = items.filter((i) => (i.lookup_key || "").startsWith("mamamina_drink_")).length;
                const food = items.length - drinks;
                const hasFamilyBundle = items.some((i) => i.lookup_key === "mamamina_bundle_family_feast");
                const hasKidsMeal = items.some((i) => i.lookup_key === "mamamina_kids_meal");
                const hasKidsDrink = items.some(
                  (i) => i.lookup_key === "mamamina_drink_kids_popper" || i.lookup_key === "mamamina_drink_kids_fruit"
                );
                const totalDrinks = items
                  .filter((i) => (i.lookup_key || "").startsWith("mamamina_drink_"))
                  .reduce((s, i) => s + i.quantity, 0);

                // Family bundle in cart but not enough drinks yet
                if (hasFamilyBundle && combo.family === 0 && totalDrinks < 4) {
                  return (
                    <div
                      data-testid="combo-nudge-family"
                      className="border border-[var(--mm-accent)]/50 bg-[var(--mm-accent)]/8 px-3 py-2 flex items-start gap-2"
                    >
                      <Tag size={12} className="text-[var(--mm-accent)] mt-[3px] shrink-0" />
                      <p className="text-xs text-[var(--mm-cream)] leading-relaxed">
                        <span className="text-[var(--mm-accent)] font-semibold tracking-wide uppercase">Family Combo:</span>{" "}
                        add {4 - totalDrinks} more drink{4 - totalDrinks === 1 ? "" : "s"} to unlock an extra $8 off.
                      </p>
                    </div>
                  );
                }
                // Kids meal in cart, no kids drink yet
                if (hasKidsMeal && !hasKidsDrink && combo.kids === 0) {
                  return (
                    <div
                      data-testid="combo-nudge-kids"
                      className="border border-[var(--mm-accent)]/50 bg-[var(--mm-accent)]/8 px-3 py-2 flex items-start gap-2"
                    >
                      <Tag size={12} className="text-[var(--mm-accent)] mt-[3px] shrink-0" />
                      <p className="text-xs text-[var(--mm-cream)] leading-relaxed">
                        <span className="text-[var(--mm-accent)] font-semibold tracking-wide uppercase">Kids Combo:</span>{" "}
                        add a Kids Juice Popper or Fruit Drink to cap it at $10 flat.
                      </p>
                    </div>
                  );
                }
                // Base meal+drink hint
                if (food > 0 && drinks === 0) {
                  return (
                    <div
                      data-testid="combo-nudge"
                      className="border border-[var(--mm-accent)]/50 bg-[var(--mm-accent)]/8 px-3 py-2 flex items-start gap-2"
                    >
                      <Tag size={12} className="text-[var(--mm-accent)] mt-[3px] shrink-0" />
                      <p className="text-xs text-[var(--mm-cream)] leading-relaxed">
                        <span className="text-[var(--mm-accent)] font-semibold tracking-wide uppercase">Combo Deal:</span> add any drink and save $2 automatically.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Promo code */}
              <div className="flex gap-2">
                <div className="flex-1 flex items-center border border-[var(--mm-line)] focus-within:border-[var(--mm-accent)]">
                  <Tag size={14} className="ml-3 text-[var(--mm-cream-muted)]" />
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Promo code (try WELCOME10)"
                    className="flex-1 bg-transparent text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-2 py-2 text-sm outline-none tracking-widest"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyPromoManual}
                  disabled={promoLoading}
                  className="px-4 py-2 border border-[var(--mm-accent)] text-[var(--mm-accent)] text-xs tracking-[0.24em] uppercase hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-all"
                >
                  {promoLoading ? "..." : "Apply"}
                </button>
              </div>
              {promo && (
                <div className="flex items-center gap-2 text-xs text-[var(--mm-accent)]">
                  <Check size={12} /> {promo.label} · −${discount.toFixed(2)}
                </div>
              )}

              {/* Order type — sent to POS on ticket */}
              <div className="grid grid-cols-3 gap-1 pt-2">
                {[
                  { key: "takeaway", label: "Takeaway" },
                  { key: "dine_in", label: "Dine In" },
                  { key: "delivery", label: "Delivery" }
                ].map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    data-testid={`order-type-${o.key}`}
                    onClick={() => setOrderType(o.key)}
                    className={`text-[0.65rem] tracking-[0.2em] uppercase py-2 border transition-all ${
                      orderType === o.key
                        ? "bg-[var(--mm-accent)] text-[var(--mm-bg)] border-[var(--mm-accent)]"
                        : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              {/* SMS language — controls what language customer notifications arrive in */}
              <div className="pt-1">
                <div className="text-[0.6rem] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2">
                  SMS language
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { key: "en", label: "English" },
                    { key: "fr", label: "Français" },
                    { key: "kri", label: "Krio" }
                  ].map((l) => (
                    <button
                      key={l.key}
                      type="button"
                      data-testid={`lang-${l.key}`}
                      onClick={() => setLanguage(l.key)}
                      className={`text-[0.62rem] tracking-[0.16em] uppercase py-2 border transition-all ${
                        language === l.key
                          ? "bg-[var(--mm-accent)] text-[var(--mm-bg)] border-[var(--mm-accent)]"
                          : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              {orderType === "dine_in" && isPreOrder && (
                <div
                  className="border border-[var(--mm-accent)]/40 bg-[var(--mm-accent)]/5 p-3 space-y-2"
                  data-testid="preorder-panel"
                >
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--mm-accent)] font-semibold">
                    Pre-Order · Kitchen holds until you arrive
                  </div>
                  <label className="block text-xs text-[var(--mm-cream-muted)]">
                    I'll be there in
                  </label>
                  <select
                    value={arrivalMinutes}
                    onChange={(e) => setArrivalMinutes(parseInt(e.target.value, 10))}
                    data-testid="preorder-arrival-select"
                    className="w-full bg-[var(--mm-bg)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
                  >
                    <option value={15}>~ 15 minutes</option>
                    <option value={30}>~ 30 minutes</option>
                    <option value={45}>~ 45 minutes</option>
                    <option value={60}>~ 1 hour</option>
                    <option value={90}>~ 1½ hours</option>
                  </select>
                  <input
                    type="text"
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                    placeholder="Table # (optional — we'll match on your name)"
                    data-testid="preorder-table-number"
                    className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-xs focus:border-[var(--mm-accent)] outline-none"
                  />
                </div>
              )}
              {orderType === "dine_in" && !isPreOrder && (
                <input
                  type="text"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  placeholder="Table number (for the kitchen)"
                  data-testid="dine-in-table-number"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
                />
              )}
              {orderType === "delivery" && (
                <>
                  <DeliveryZoneCheck onZoneChange={setDeliveryZone} />
                  <input
                    type="text"
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Street address + unit number"
                    data-testid="delivery-address-input"
                    className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
                  />
                </>
              )}

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for receipt (optional)"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
              />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Mobile for order updates (recommended)"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
              />
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Notes (allergies, spice level...)"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none resize-none"
              />
            </div>
          )}
        </div>

        {/* Sticky Pay footer — always visible above the fold */}
        {items.length > 0 && (
          <div
            className="border-t border-[var(--mm-line)] p-4 shrink-0 bg-[var(--mm-bg)] space-y-3"
            data-testid="cart-pay-footer"
          >
            <div className="flex justify-between items-baseline">
              <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)]">Subtotal</span>
              <span className="text-[var(--mm-cream)]">${total.toFixed(2)}</span>
            </div>
            {promo && (
              <div className="flex justify-between items-baseline">
                <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-accent)]">Discount ({promo.percent_off}%)</span>
                <span className="text-[var(--mm-accent)]">−${discount.toFixed(2)}</span>
              </div>
            )}
            {!promo && combo.family > 0 && (
              <div
                className="flex justify-between items-baseline"
                data-testid="cart-family-combo-savings"
                title={`${combo.family} Family Feast + 4 drinks combo${combo.family === 1 ? "" : "s"}`}
              >
                <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-accent)] flex items-center gap-1.5">
                  <Tag size={11} /> Family Combo ×{combo.family}
                </span>
                <span className="text-[var(--mm-accent)]">−${combo.familyAmt.toFixed(2)}</span>
              </div>
            )}
            {!promo && combo.kids > 0 && (
              <div
                className="flex justify-between items-baseline"
                data-testid="cart-kids-combo-savings"
                title={`${combo.kids} Kids meal + drink combo${combo.kids === 1 ? "" : "s"} capped at $10`}
              >
                <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-accent)] flex items-center gap-1.5">
                  <Tag size={11} /> Kids Combo ×{combo.kids}
                </span>
                <span className="text-[var(--mm-accent)]">−${combo.kidsAmt.toFixed(2)}</span>
              </div>
            )}
            {!promo && combo.base > 0 && (
              <div
                className="flex justify-between items-baseline"
                data-testid="cart-combo-savings"
                title={`${combo.base} meal + drink combo${combo.base === 1 ? "" : "s"}`}
              >
                <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-accent)] flex items-center gap-1.5">
                  <Tag size={11} /> Combo Deal ×{combo.base}
                </span>
                <span className="text-[var(--mm-accent)]">−${combo.baseAmt.toFixed(2)}</span>
              </div>
            )}
            {deliveryFee > 0 && (
              <div className="flex justify-between items-baseline" data-testid="cart-delivery-fee">
                <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)]">
                  Delivery ({deliveryZone.zone_name})
                </span>
                <span className="text-[var(--mm-cream)]">+${deliveryFee.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline">
              <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)]">Total</span>
              <span className="font-display text-2xl text-[var(--mm-accent)]">${totalAfter.toFixed(2)} AUD</span>
            </div>
            {belowMinOrder && (
              <div
                data-testid="cart-min-order-warning"
                className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300 leading-snug"
              >
                <span className="font-semibold uppercase tracking-wide text-red-200">Minimum order not met · </span>
                Add ${(deliveryZone.min_order - totalPreDelivery).toFixed(2)} more to unlock delivery to {deliveryZone.zone_name}, or switch to Takeaway.
              </div>
            )}
            {referralInfo && referralInfo.valid && (
              <div
                data-testid="cart-referral-banner"
                className="border border-[var(--mm-accent)]/50 bg-[var(--mm-accent)]/10 px-3 py-2 text-xs text-[var(--mm-cream)] leading-snug flex items-center gap-2"
              >
                <Gift size={13} className="text-[var(--mm-accent)] shrink-0" />
                <span>
                  <span className="text-[var(--mm-accent)] font-semibold uppercase tracking-wide">
                    ${referralInfo.discount} friend discount ·{" "}
                  </span>
                  Sent by {referralInfo.referrer_first_name}. Applied at checkout.
                </span>
              </div>
            )}
            <button
              onClick={checkout}
              disabled={loading || belowMinOrder}
              data-testid="cart-pay-btn"
              className="btn-primary w-full text-center flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Redirecting..." : "Pay with Stripe"}
            </button>
            <p className="text-[10px] text-[var(--mm-cream-muted)] font-light leading-relaxed text-center">
              Taxes calculated at checkout. Secure payment powered by Stripe.
            </p>
          </div>
        )}
      </aside>
    </>
  );
}

function Step({ n, label, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[0.65rem] font-semibold ${done ? "bg-[var(--mm-accent)] text-[var(--mm-bg)]" : "border border-[var(--mm-line)] text-[var(--mm-cream-muted)]"}`}>
        {done ? <Check size={11} /> : n}
      </div>
      <span className={`text-[0.62rem] tracking-[0.22em] uppercase ${done ? "text-[var(--mm-accent)]" : "text-[var(--mm-cream-muted)]"}`}>{label}</span>
    </div>
  );
}
